"use client";

import { createContext, createElement, type ReactNode, useContext, useMemo } from "react";

/**
 * A ceiling on how many tile fetches may be in flight ACROSS every streaming
 * chart on the page at once.
 *
 * ── 🔴 The gap this closes, precisely ─────────────────────────────────────────
 * `useTileLoader` already bounds concurrency — but its `inflight` map is a
 * `useRef` inside one hook instance, so the bound is PER CHART. One chart asks
 * for at most 3 slices at a time; twelve charts ask for 36, and nothing
 * anywhere knows that. The same is true of the existing `COST_GUARD`, which
 * caps overscan within a single chart's ladder.
 *
 * That was a safe omission while exactly one surface streamed. It stops being
 * safe the moment a board — where the reader chooses how many tiles to place —
 * can hold several streaming charts, because the cost of a board is a product
 * of a number the reader picks and a fan-out no single chart can see.
 *
 * ── Why a ceiling on CONCURRENCY rather than a cap on rows ────────────────────
 * Measured against the real meter ladder, one chart's visible window costs
 * about 4k rows at the 1h tier while the per-chart guard would permit ~250k.
 * Rows are not what runs out first; simultaneous requests are. A row budget
 * would therefore either never bind or, set low enough to bind, would truncate
 * history a reader explicitly asked for — which is a worse failure than making
 * them wait.
 *
 * ── What it deliberately does NOT do ──────────────────────────────────────────
 * It never drops a fetch, shortens a window, or coarsens a tier. Every slice a
 * chart asks for is still fetched; a slice over the ceiling simply WAITS for a
 * permit and runs when one frees up. The reader sees the same data, arriving in
 * a slightly different order — which is why this is safe to apply globally
 * rather than only on boards. A guard that silently served less data than was
 * asked for would be the quiet kind of wrong this codebase keeps finding.
 */
export interface ChartFetchBudget {
  /**
   * Wait for permission to start one fetch. Resolves immediately when the page
   * is under its ceiling, and otherwise when an earlier fetch releases.
   *
   * Always paired with the returned release in a `finally` — a permit leaked on
   * an error path is permanently lost capacity, and the symptom is charts that
   * simply stop loading with nothing in the console.
   *
   * ── 🔴 `permits` is what keeps the ceiling honest under fan-out ─────────────
   * One `fetchSlice` is not always one HTTP request. The grid cohort's slice
   * issues three — net, then consumption and generation together — because the
   * hook it wraps exposes them as three loaders and the island beside it still
   * wants them apart. Charged one permit each, twelve such slices would put 36
   * requests on the wire while this pool reported 12: the ceiling defeated
   * precisely on the boards it exists for.
   *
   * So a slice that fans out declares it (`ResolvedChartSource.fetchesPerSlice`)
   * and holds that many permits for its duration.
   */
  acquire(permits?: number): Promise<() => void>;
  /**
   * Permits currently outstanding, and how many callers are queued behind them.
   *
   * Reported rather than inferred: a test that counted concurrent fetches by
   * wrapping the fetcher would measure its own wrapper, and the queue depth —
   * the thing that proves the ceiling actually bound — is not observable from
   * outside at all.
   */
  readonly stats: { active: number; waiting: number };
}

/**
 * How many tile fetches may run at once across the whole page.
 *
 * 12 is three concurrent charts' worth at the loader's own default of 3, chosen
 * so that the common surfaces are entirely unaffected: an entity page shows one
 * streaming chart, Explore shows one, and a small board of two or three never
 * reaches the ceiling. It binds only where the unbounded case actually lives —
 * a board holding more streaming tiles than a browser will usefully fetch in
 * parallel anyway.
 */
export const DEFAULT_MAX_CONCURRENT_CHART_FETCHES = 12;

/**
 * The permit pool.
 *
 * A plain FIFO queue rather than anything cleverer: fetches are already ordered
 * by the loader nearest-to-centre, so the useful priority decision has been
 * made by the time a request arrives here. Re-ordering it a second time would
 * only undo that.
 */
export function createChartFetchBudget(maxConcurrent: number): ChartFetchBudget {
  /*
   * 🔴 `NaN` has to be rejected explicitly, not clamped.
   *
   * `Math.max(1, Math.floor(NaN))` is `NaN`, and `active < NaN` is false for
   * every value of `active` — so a NaN ceiling does not fall back to one slot,
   * it deadlocks every chart on the page permanently, with no error and no
   * console output. The symptom is charts that spin forever, which is precisely
   * the failure this guard exists to avoid causing.
   */
  const limit = Number.isFinite(maxConcurrent) ? Math.max(1, Math.floor(maxConcurrent)) : 1;
  let active = 0;
  /** Each waiter remembers how many permits it is queued FOR, not just that it waits. */
  const waiting: Array<{ permits: number; grant: () => void }> = [];

  /*
   * 🔴 Strictly FIFO, and it stops at the head rather than scanning past it.
   *
   * The tempting alternative — skip a waiter that does not fit and start a
   * smaller one behind it — starves exactly the charts this guard was built
   * for: a fan-out slice needing 3 permits would be passed over indefinitely
   * while single-permit slices kept arriving. Head-of-line blocking is the
   * lesser cost, and it is bounded, because every holder releases.
   */
  const pump = () => {
    while (waiting.length > 0) {
      const next = waiting[0];
      if (active + next.permits > limit) return;
      waiting.shift();
      active += next.permits;
      next.grant();
    }
  };

  /*
   * 🔴 Release must be idempotent. `useTileLoader` aborts in-flight requests on
   * reset, and an abort can unwind a path that has already released; a second
   * decrement would hand out permits that were never returned and inflate the
   * ceiling permanently. `once` around each grant is what guarantees it.
   */
  const releaseOf = (permits: number) => () => {
    active = Math.max(0, active - permits);
    pump();
  };

  return {
    acquire(permits = 1) {
      /*
       * Clamped to the ceiling, never rejected. A slice that fans out wider
       * than the whole page's budget must still run — refusing it would drop
       * data, which is the one thing this guard promises never to do. It simply
       * becomes the only thing in flight while it runs.
       */
      const want = Math.min(limit, Number.isFinite(permits) ? Math.max(1, Math.floor(permits)) : 1);
      // Queue behind existing waiters even when there is room, or a late
      // single-permit caller would overtake a fan-out slice already in line.
      if (waiting.length === 0 && active + want <= limit) {
        active += want;
        return Promise.resolve(once(releaseOf(want)));
      }
      return new Promise<() => void>((resolve) => {
        waiting.push({ permits: want, grant: () => resolve(once(releaseOf(want))) });
      });
    },
    get stats() {
      return { active, waiting: waiting.length };
    },
  };
}

/**
 * The rejection an aborted fetch produces, matching what `fetch` itself throws
 * so the loader's existing abort handling treats it identically.
 */
function abortError(): Error {
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  return error;
}

/** Guards a release against being called twice; see the note in `release`. */
function once(fn: () => void): () => void {
  let called = false;
  return () => {
    if (called) return;
    called = true;
    fn();
  };
}

/**
 * An unlimited budget, for the paths that must not queue.
 *
 * Returned when no provider is mounted so that a chart rendered in a test, a
 * docs page or a screenshot harness behaves exactly as it did before this
 * existed.
 */
/** Releasing an unlimited permit is a no-op: nothing was ever held. */
const releaseNothing = () => {
  /* no permit to return */
};

export const UNLIMITED_CHART_FETCH_BUDGET: ChartFetchBudget = {
  acquire: () => Promise.resolve(releaseNothing),
  stats: { active: 0, waiting: 0 },
};

const ChartFetchBudgetContext = createContext<ChartFetchBudget | null>(null);

export interface ChartFetchBudgetProviderProps {
  /** Override the page-wide ceiling. Omit for the default. */
  maxConcurrent?: number;
  children: ReactNode;
}

/**
 * Installs one shared budget for everything rendered beneath it.
 *
 * Mounted by `ChartSourceProvider`, because the two have exactly the same
 * scope: anything that can resolve a streaming source is something whose
 * fetches this must count.
 */
export function ChartFetchBudgetProvider({ maxConcurrent, children }: ChartFetchBudgetProviderProps) {
  /*
   * 🔴 A nested provider REUSES the ceiling above it rather than opening a
   * second one.
   *
   * The whole value of this budget is that it is shared: two independent pools
   * of 12 are just a ceiling of 24 that nobody declared, which is the exact
   * failure it exists to prevent. `ChartSourceProvider` mounts this, and an app
   * that nests those — a board tile that installs its own resolver, say — would
   * otherwise silently double the page's fetch capacity.
   *
   * An explicit `maxConcurrent` still wins, because that is someone asking for
   * a different ceiling on purpose rather than nesting by accident.
   */
  const inherited = useContext(ChartFetchBudgetContext);
  const budget = useMemo(() => {
    if (maxConcurrent !== undefined) return createChartFetchBudget(maxConcurrent);
    return inherited ?? createChartFetchBudget(DEFAULT_MAX_CONCURRENT_CHART_FETCHES);
  }, [maxConcurrent, inherited]);
  return createElement(ChartFetchBudgetContext.Provider, { value: budget }, children);
}

/**
 * The installed budget, or an unlimited one when no provider is mounted.
 *
 * Never null, so no caller has to branch — an absent provider means "this tree
 * has no ceiling", which is the correct reading outside an app.
 */
export function useChartFetchBudget(): ChartFetchBudget {
  return useContext(ChartFetchBudgetContext) ?? UNLIMITED_CHART_FETCH_BUDGET;
}

/**
 * Wrap a fetcher so each call holds a permit for its duration.
 *
 * 🔴 The permit is acquired BEFORE the fetch and released in a `finally`, so an
 * error or an abort returns it. The wrapped function keeps the original's
 * signature exactly, which is what lets this apply at one seam without any
 * source, block or host learning that a budget exists.
 */
export function withFetchBudget<TArgs extends unknown[], TResult>(
  budget: ChartFetchBudget,
  fetcher: (...args: TArgs) => Promise<TResult>,
  /** Underlying requests one call makes; see `acquire`'s note on fan-out. */
  permits = 1
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs) => {
    /*
     * 🔴 An aborted caller must not consume its turn.
     *
     * `useTileLoader` aborts in-flight and queued work whenever the reader pans
     * or the source resets — and a queued fetch can sit behind the ceiling for
     * a while. Without this check it waits, takes a permit, and then runs a
     * fetch whose result is discarded, holding a slot a live chart needed.
     * Under a fast pan that is the whole queue doing wasted work.
     *
     * `FetchTile`'s signal is its fourth argument; anything wrapped without one
     * behaves exactly as before.
     */
    const signal = args[3];
    const aborted = () => signal instanceof AbortSignal && signal.aborted;
    if (aborted()) throw abortError();

    const release = await budget.acquire(permits);
    try {
      // Re-checked AFTER the wait: the abort usually lands while queued.
      if (aborted()) throw abortError();
      return await fetcher(...args);
    } finally {
      release();
    }
  };
}
